import React, { useEffect, useMemo, useState } from 'react';

import { useDocumentTitle } from './document.component';
import { useCVLanguage } from './i18n/language.component';
import { CVEnglishOnly, CVFavicon, CVTopbar, CVFooter, contentLanguage, useCaptiVisionTheme, scrollToId } from './shell.component';

import './captivision.styles.scss';

// The frame for long document pages - the full guide and the tester
// instructions: a contents rail down the left that follows the reader, the
// document header, and the article. Shorter pages use CVDocument instead. Like
// it, `lang` is the language the content is in, and until it is known only the
// chrome is drawn. The tester instructions are in English only.

// A named on-screen control, set the way the apps label it
export const UI = ({ children }) => <span className="cv-ui">{children}</span>

// an in-page link that scrolls under the sticky top bar rather than jumping
export const Jump = ({ to, children }) => (
    <a href={`#${to}`} onClick={event => { event.preventDefault(); scrollToId(to) }}>{children}</a>
)

// ruled two-column rows; `rows` is a list of [name, body]
export const Rows = ({ head, rows }) => (
    <div className="cv-rowswrap">
        <table className="cv-rows">
            {head ? <thead><tr><th> {head[0]} </th><th> {head[1]} </th></tr></thead> : null}
            <tbody>
                {rows.map(([name, body]) => (
                    <tr key={name}><th> {name} </th><td> {body} </td></tr>
                ))}
            </tbody>
        </table>
    </div>
)

// Highlights the section being read. Of the headings currently on screen, the
// one with the most of itself visible wins.
const useScrollSpy = ids => {
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (!('IntersectionObserver' in window)) {
            return
        }

        const visible = {};
        const targets = ids.map(id => document.getElementById(id)).filter(Boolean);

        const spy = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                visible[entry.target.id] = entry.isIntersecting ? entry.intersectionRatio : 0
            })

            let best = null;
            let bestRatio = 0;

            targets.forEach(target => {
                const ratio = visible[target.id] || 0;

                if (ratio > bestRatio) {
                    bestRatio = ratio;
                    best = target.id;
                }
            })

            if (best) {
                setActive(best);
            }
        }, { rootMargin: '-80px 0px -55% 0px', threshold: [0, 0.1, 0.5, 1] });

        targets.forEach(target => spy.observe(target));

        return () => spy.disconnect()
    }, [ids])

    return active
}

// `contents` is [{ group, links: [{ id, label }] }], and every id must be a
// heading on the page. `head` is what goes in the document header.
const CVRailDocument = ({ pageTitle, className, contents = [], railLabel, navCurrent, footerCurrent, lang, head, children }) => {
    const [theme, toggleTheme] = useCaptiVisionTheme();
    const { t } = useCVLanguage();
    const [railOpen, setRailOpen] = useState(false);

    // the same headings in every language, so the spy restarts only when they arrive
    const idList = contents.flatMap(group => group.links.map(link => link.id)).join(' ');
    const ids = useMemo(() => (idList ? idList.split(' ') : []), [idList]);
    const active = useScrollSpy(ids);

    useDocumentTitle(pageTitle)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const jump = (event, id) => {
        event.preventDefault();
        setRailOpen(false);
        scrollToId(id);
    }

    const contentsButton = (
        <button className="cv-btn cv-btn-sm cv-toc-btn" type="button" aria-expanded={railOpen} onClick={() => setRailOpen(open => !open)}>
            {t.docs.contents}
        </button>
    )

    return (
        <div className={`captivision ${className}`}>
            <CVFavicon />
            <CVTopbar theme={theme} onToggleTheme={toggleTheme} current={navCurrent} wide extra={contentsButton} />
            <CVEnglishOnly lang={lang} />

            <div className="cv-shell" {...(lang ? contentLanguage(lang) : {})}>
                <aside className={`cv-rail${railOpen ? ' is-open' : ''}`} aria-label={railLabel}>
                    {contents.map(group => (
                        <React.Fragment key={group.group}>
                            <h4> {group.group} </h4>
                            <ol>
                                {group.links.map(link => (
                                    <li key={link.id}>
                                        <a href={`#${link.id}`} className={active === link.id ? 'is-active' : undefined} onClick={event => jump(event, link.id)}>
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </React.Fragment>
                    ))}
                </aside>

                <main className="cv-doc">
                    <header className="cv-dochead">
                        {head}
                    </header>

                    <article className="cv-prose">
                        {children}
                    </article>
                </main>
            </div>

            <CVFooter current={footerCurrent} wide />
        </div>
    )
}

export default CVRailDocument;
